'use client';

import { useState, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import LanguageSwitcher from './LanguageSwitcher';
import MobileDrawer from './MobileDrawer';
import NavbarDropdown from './NavbarDropdown';

export default function Navbar() {
  const t = useTranslations('navigation');
  const locale = useLocale();
  const pathname = usePathname();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10); // Increased threshold to 10px
    };

    // Set initial scroll state
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const withLocale = (path: string) => (path === '/' ? `/${locale}` : `/${locale}${path}`);

  const navItems = [
    { href: withLocale('/'), label: t('home') },
    { href: withLocale('/about'), label: t('about') },
    { href: withLocale('/notices'), label: t('notices') },
    { 
      href: '#', 
      label: t('gallery'),
      children: [
        { href: withLocale('/sports'), label: t('sports') },
        { href: withLocale('/music'), label: t('music') },
        { href: withLocale('/green-school'), label: t('greenSchool') },
      ]
    },
    { href: withLocale('/contact'), label: t('contact') },
  ];

  const isActive = (href: string) => {
    if (href === '/') {
      return pathname === '/' || pathname.endsWith('/');
    }
    return pathname.includes(href);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[#bb1b1b] shadow-lg' // Always use your brand color when scrolled
            : 'bg-white/80 backdrop-blur-sm'
        }`}
      >
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link href={withLocale('/')} className="flex items-center">
              <div className="h-16 md:h-20 relative">
                <Image
                  src={isScrolled ? "/images/logo-right3.png" : "/images/logo-right5.png"}
                  alt="Scoil Colmcille Naofa Logo"
                  width={200}
                  height={64}
                  className="h-full w-auto object-contain transition-all duration-300"
                  priority
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-10">
              {navItems.map((item) => (
                item.children ? (
                  <NavbarDropdown key={item.href} isScrolled={isScrolled} />
                ) : (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`text-base font-medium transition-colors duration-200 ${
                      isActive(item.href.replace(`/${locale}`, ''))
                        ? isScrolled
                          ? 'text-white' // White when active and scrolled
                          : 'text-[#bb1b1b]' // Red when active and at top
                        : isScrolled
                        ? 'text-white hover:text-gray-200'
                        : 'text-[#bb1b1b] hover:text-[#bb1b1b]/80'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              ))}
            </div>

            {/* Language Switcher and Mobile Menu Button */}
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className={`lg:hidden p-2 rounded-md transition-colors duration-200 ${
                  isScrolled
                    ? 'text-white hover:text-gray-200 hover:bg-white/20'
                    : 'text-[#bb1b1b] hover:text-[#bb1b1b]/80 hover:bg-[#bb1b1b]/10'
                }`}
                aria-label="Open mobile menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <MobileDrawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navItems={navItems}
        isActive={isActive}
      />
    </>
  );
}
